import { useEffect, useState } from 'react'

// Course feedback: 1–5 stars, optional comment, Submit. Used after the hands-on labs of every course
// (team courses and CAMS prep modules) and after the CAMS final exam.
export default function CourseRating({ moduleId, courseTitle, onSubmitted }) {
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(null) // the stored rating once submitted
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/training/course-rating?moduleId=${encodeURIComponent(moduleId)}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d?.rating) return
        setSaved(d); setRating(d.rating); setComment(d.comment || '')
        onSubmitted?.(d)
      })
      .catch(() => null)
    return () => { cancelled = true }
  }, [moduleId])

  const submit = async () => {
    if (!rating) { setError('Choose a rating from 1 to 5 stars.'); return }
    setSaving(true); setError(null)
    try {
      const r = await fetch('/api/training/course-rating', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleId, rating, comment: comment.trim() }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || 'Could not save your rating')
      setSaved(d); onSubmitted?.(d)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const labels = ['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent']
  const shown = hover || rating

  if (saved && !saving) {
    return (
      <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 8, background: '#ecfdf5', border: '1px solid #a7f3d0' }}>
        <strong style={{ color: '#065f46' }}>✓ Thank you for rating this course</strong>
        <div style={{ color: '#f59e0b', fontSize: 20, letterSpacing: 2, margin: '4px 0' }} aria-label={`${saved.rating} out of 5 stars`}>
          {'★'.repeat(saved.rating)}<span style={{ color: '#d1d5db' }}>{'★'.repeat(5 - saved.rating)}</span>
        </div>
        {saved.comment && <div style={{ fontSize: 13, color: '#374151' }}>“{saved.comment}”</div>}
        <button onClick={() => setSaved(null)} style={{ marginTop: 8, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: 13 }}>Change my rating</button>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 16, padding: '16px 18px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <h4 style={{ margin: '0 0 4px', color: '#0A2342' }}>Rate this course</h4>
      <p style={{ margin: '0 0 10px', fontSize: 13, color: '#4b5563' }}>How would you rate {courseTitle ? <strong>{courseTitle}</strong> : 'this course'}? Your rating is required to complete the course.</p>
      <div role="radiogroup" aria-label="Course rating" style={{ display: 'flex', alignItems: 'center', gap: 4 }} onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} star${n > 1 ? 's' : ''} — ${labels[n]}`}
            onClick={() => { setRating(n); setError(null) }} onMouseEnter={() => setHover(n)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 30, lineHeight: 1, padding: '0 2px', color: n <= shown ? '#f59e0b' : '#d1d5db' }}>★</button>
        ))}
        <span style={{ marginLeft: 8, fontSize: 13, color: '#4b5563', minWidth: 70 }}>{shown ? labels[shown] : ''}</span>
      </div>
      <label style={{ display: 'block', marginTop: 10, fontSize: 13, color: '#374151' }}>
        Comments (optional)
        <textarea value={comment} onChange={e => setComment(e.target.value.slice(0, 2000))} rows={3}
          placeholder="What worked well? What could be better?"
          style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontFamily: 'inherit', fontSize: 14 }} />
      </label>
      {error && <p role="alert" style={{ color: '#b91c1c', fontSize: 13, margin: '8px 0 0' }}>{error}</p>}
      <button onClick={submit} disabled={saving}
        style={{ marginTop: 10, padding: '9px 20px', borderRadius: 6, border: 'none', background: rating ? '#0A2342' : '#94a3b8', color: '#fff', fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
        {saving ? 'Submitting…' : 'Submit'}
      </button>
    </div>
  )
}
